/**
 * WorkflowRibbon — Three-Act chevron pipeline
 *
 * Full-width ribbon at the top of the workspace. Three connected
 * chevron/arrow shapes (ACT I amber → ACT II blue → ACT III dark)
 * each containing stage pill buttons for that act.
 */
import React, { useMemo } from 'react';
import {
  Lightbulb, Map, Users, FileText, PenLine, BookOpen,
  RefreshCw, Sparkles, Check, Globe, HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';
import { useContextRebuild } from '@/hooks/useContextRebuild';
import { toast } from 'sonner';

/* ─── Stage definitions ─────────────────────────────────────────── */

const STAGES = [
  {
    id: 'idea', label: 'Idea', icon: Lightbulb,
    desc: 'Genre, tone & story premise',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'variables' })),
  },
  {
    id: 'characters', label: 'Characters', icon: Users,
    desc: 'Cast & character bible',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'bible' })),
  },
  {
    id: 'worldBible', label: 'World Bible', icon: Globe,
    desc: 'Setting, themes & world-building',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'variables' })),
  },
  {
    id: 'plot', label: 'Plot Blueprint', icon: Map,
    desc: 'Define the 6 structural beats before drafting begins',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-plot-builder')),
  },
  {
    id: 'synopsis', label: 'Synopsis', icon: FileText,
    desc: 'Story synopsis & chapter outline',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-synopsis')),
  },
  {
    id: 'write', label: 'Draft', icon: PenLine,
    desc: 'AI-assisted chapter drafting',
    action: () => window.dispatchEvent(new CustomEvent('loom:draft-requested')),
  },
  {
    id: 'read', label: 'Arc Review', icon: BookOpen,
    desc: 'Arc Walker — read & score your draft',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-arc-walker')),
  },
  {
    id: 'rewrite', label: 'Rewrite', icon: RefreshCw,
    desc: 'Edit Walker — structural & developmental edits',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-edit-walker')),
  },
  {
    id: 'prose', label: 'Polish', icon: Sparkles,
    desc: 'Manuscript Polish — line-level prose edit',
    action: () => window.dispatchEvent(new CustomEvent('loom:open-flow-pass')),
  },
];

const ACT_GROUPS = [
  {
    act: 'I',  label: 'ACT I',
    stageIds: ['idea', 'characters', 'worldBible'],
    bg:         'bg-amber-600',
    activePill: 'bg-white text-amber-800 font-bold shadow',
  },
  {
    act: 'II', label: 'ACT II',
    stageIds: ['plot', 'synopsis', 'write'],
    bg:         'bg-blue-700',
    activePill: 'bg-white text-blue-800 font-bold shadow',
  },
  {
    act: 'III', label: 'ACT III',
    stageIds: ['read', 'rewrite', 'prose'],
    bg:         'bg-zinc-600',
    activePill: 'bg-white text-zinc-800 font-bold shadow',
  },
];

const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.id, s]));
const ARROW     = 22; // px — depth of each chevron arrow point

/* ─── Completion hook ────────────────────────────────────────────── */

function useWorkflowStage() {
  const { project, chapters = [], characters = [], variables = {} } = usePW();

  return useMemo(() => {
    if (!project) return { current: 'idea', completed: new Set() };

    const hasIdea       = !!(variables?.genre || variables?.tone || project?.genre || project?.logline || project?.plot_score > 0);
    const hasCharacters = characters.length > 0;
    const hasWorldBible = !!(variables?.themes || variables?.setting || hasCharacters || hasIdea);
    const hasPlot       = !!(project?.plot_extraction?.extracted_at);
    const hasSynopsis   = !!(project?.synopsis && project.synopsis.trim().length > 20);
    const drafted       = chapters.filter((c) => (c.word_count || 0) > 50 || (c.content || '').length > 100);
    const hasWrite      = drafted.length > 0;
    const hasRead       = !!(project?.arc_walker_result?.publishable_score);
    const hasRewrite    = !!localStorage.getItem(`cr_editwalk_done_${project.id}`);
    const hasProse      = !!localStorage.getItem(`cr_polish_done_${project.id}`);

    const completed = new Set();
    if (hasIdea)       completed.add('idea');
    if (hasCharacters) completed.add('characters');
    if (hasWorldBible) completed.add('worldBible');
    if (hasPlot)       completed.add('plot');
    if (hasSynopsis)   completed.add('synopsis');
    if (hasWrite)      completed.add('write');
    if (hasRead)       completed.add('read');
    if (hasRewrite)    completed.add('rewrite');
    if (hasProse)      completed.add('prose');

    const order   = ['idea', 'characters', 'worldBible', 'plot', 'synopsis', 'write', 'read', 'rewrite', 'prose'];
    const current = order.find((s) => !completed.has(s)) || 'prose';
    return { current, completed };
  }, [project, chapters, characters, variables]);
}

/* ─── Chevron act section ────────────────────────────────────────── */

function ChevronAct({ group, stages, current, completed, isFirst, isLast, index }) {
  const clipFirst  = `polygon(0 0, calc(100% - ${ARROW}px) 0, 100% 50%, calc(100% - ${ARROW}px) 100%, 0 100%)`;
  const clipMiddle = `polygon(${ARROW}px 0, calc(100% - ${ARROW}px) 0, 100% 50%, calc(100% - ${ARROW}px) 100%, 0 100%, 0 50%)`;
  const clipLast   = `polygon(${ARROW}px 0, 100% 0, 100% 100%, 0 100%, 0 50%)`;
  const clip       = isFirst ? clipFirst : isLast ? clipLast : clipMiddle;

  return (
    <div
      data-testid={`workflow-act-${group.act}`}
      className={cn('relative flex flex-1 flex-col justify-center select-none', group.bg)}
      style={{
        clipPath:    clip,
        zIndex:      10 - index,
        marginLeft:  isFirst ? 0 : -ARROW,
        paddingLeft:  isFirst ? 14 : ARROW + 10,
        paddingRight: isLast  ? 12 : ARROW + 6,
        paddingTop:   4,
        paddingBottom: 4,
      }}
    >
      {/* Act label */}
      <span className="mb-1 block text-[8px] font-black uppercase tracking-[0.22em] text-white/60 leading-none">
        {group.label}
      </span>

      {/* Stage pills */}
      <div className="flex flex-wrap gap-1">
        {stages.map((stage) => {
          const isDone    = completed.has(stage.id);
          const isCurrent = current === stage.id;

          return (
            <button
              key={stage.id}
              type="button"
              title={stage.desc}
              onClick={stage.action}
              data-testid={`workflow-stage-${stage.id}`}
              className={cn(
                'flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-all',
                isDone    && !isCurrent && 'bg-white/20 text-white',
                isCurrent && group.activePill,
                !isDone   && !isCurrent && 'bg-black/20 text-white/45 hover:text-white/70 hover:bg-black/30',
              )}
            >
              {isDone && <Check className="h-2.5 w-2.5 shrink-0" />}
              {stage.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */

export function WorkflowRibbon() {
  const { current, completed } = useWorkflowStage();
  const { hasPending, rebuild }  = useContextRebuild();
  const nextStage = STAGES.find((s) => s.id === current);

  function handleRebuild() {
    rebuild();
    toast.success('AI context updated', {
      description: 'Future suggestions and generation will use your latest characters, variables and plot.',
      duration: 3500,
    });
  }

  return (
    <div
      className="flex shrink-0 flex-col border-b border-chrome-border bg-chrome"
      data-testid="workflow-ribbon"
      role="navigation"
      aria-label="Writing workflow stages"
    >
      {/* ── Chevron pipeline ─────────────────────────────────────── */}
      <div className="flex h-[58px] overflow-visible">
        {ACT_GROUPS.map((group, index) => {
          const stages = group.stageIds.map((id) => STAGE_MAP[id]).filter(Boolean);
          return (
            <ChevronAct
              key={group.act}
              group={group}
              stages={stages}
              current={current}
              completed={completed}
              isFirst={index === 0}
              isLast={index === ACT_GROUPS.length - 1}
              index={index}
            />
          );
        })}

        {/* Writing Coach help */}
        <button
          type="button"
          data-testid="workflow-coach-help-btn"
          title="Open Writing Coach"
          onClick={() => {
            const pid = window.__loom_project_id__;
            if (pid) { try { localStorage.removeItem(`loom:coach-dismissed-${pid}`); } catch { /* ok */ } }
            window.dispatchEvent(new CustomEvent('loom:open-coach-card'));
          }}
          className="ml-1 flex shrink-0 items-center self-center justify-center rounded-full border border-dashed border-foreground/20 p-1.5 text-foreground/30 transition-all hover:border-primary/60 hover:text-primary"
        >
          <HelpCircle className="h-3 w-3" />
        </button>

        {/* Rebuild AI Context — glows amber when foundation panels have pending changes */}
        <button
          type="button"
          data-testid="workflow-rebuild-context-btn"
          onClick={handleRebuild}
          title={hasPending ? 'Foundation data has changed — click to apply to AI' : 'AI context is up to date'}
          className={cn(
            'ml-2 mr-2 flex shrink-0 items-center gap-1.5 self-center rounded-md px-3 py-1 text-[11px] font-bold transition-all',
            hasPending
              ? 'animate-pulse bg-amber-500 text-white shadow-lg shadow-amber-500/40 hover:animate-none hover:bg-amber-400'
              : 'border border-foreground/15 bg-foreground/5 text-foreground/25 hover:bg-foreground/10 hover:text-foreground/50',
          )}
        >
          <RefreshCw className={cn('h-3 w-3 shrink-0', hasPending && 'animate-spin-slow')} />
          {hasPending ? 'Rebuild' : 'Rebuilt'}
        </button>
      </div>

      {/* ── Next step hint bar ──────────────────────────────────── */}
      {nextStage && (
        <button
          type="button"
          onClick={nextStage.action}
          data-testid="workflow-next-step-btn"
          className="group flex items-center gap-2 bg-foreground/8 px-4 py-1 text-left text-[10.5px] text-foreground/50 transition-colors hover:bg-foreground/12 hover:text-foreground/80"
        >
          <span className="font-bold text-foreground/30">→</span>
          <span>
            <strong className="font-semibold text-foreground/70">Next:</strong>
            {' '}{nextStage.desc}
          </span>
        </button>
      )}
    </div>
  );
}
